import { gql } from "@apollo/client";

export const ADD_REMOTELOCK_DEVICES_TO_PROPERTIES_GQL =
  "addRemotelockDevicesToProperties";
export const CREATE_ORGANIZATION_GQL = "createOrganization";
export const CREATE_BUILDING_GQL = "createBuilding";
export const EDIT_BUILDING_GQL = "editBuilding";
export const EDIT_ORGANIZATION_GQL = "editOrganization";
export const REMOVE_REMOTELOCK_DEVICES_TO_PROPERTIES_GQL =
  "removeRemotelockDevicesFromProperties";
export const REMOVE_PROVIEW_HOME_GQL = "removeProViewHome";
export const RESET_INSTALLATION_GQL = "resetUnitInstallationState";
export const UPDATE_CARD_CREDENTIALS_SQL = "updateCardCredentials";
export const ADD_HOME_FOR_REGION_GQL = "addHomeForRegion";
export const GET_REMOTELOCK_BUILDING_GQL = "GetRemoteLockBuilding";
export const REBOOT_BRILLIANT_CONTROL_GQL = "rebootDevice";
export const UPDATE_HOME_PASSCODE_GQL = "updateHomePasscode";
export const GET_DOOR_GROUPS_AND_UNIT_ACCESS_DEVICES_FOR_HOME_GQL =
  "GetDoorGroups";
export const GET_INTEGRATIONS_FOR_ORGANIZATION_GQL =
  "GetIntegrationsForOrganization";
export const GET_INTEGRATION_TYPE_FOR_ORGANIZATION_GQL =
  "GetIntegrationTypesForOrganization";
export const UPDATE_USER_NOTIFICATION_SETTINGS_SQL_MUTATION =
  "updateUserPropertyNotificationSetting";
export const GET_USER_OPERATION = "GetUser";

const EXTREME_TEMPERATURE_FRAGMENT = gql`
  fragment ExtremeTemperatureFragment on ExtremeTemperatureClimateAlarm {
    ambientTemperatureF
  }
`;

const REMOTELOCK_INTEGRATION_DEVICE_FRAGMENT = gql`
  fragment RemoteLockIntegrationDeviceFragment on RemoteLockIntegrationDevice {
    isCommonArea
  }
`;

const LOW_BATTERY_FRAGMENT = gql`
  fragment LowBatteryFragment on LowBatteryAlert {
    deviceType
  }
`;

const HOME_ALERTS_FRAGMENT = gql`
  ${EXTREME_TEMPERATURE_FRAGMENT}
  ${LOW_BATTERY_FRAGMENT}
  fragment HomeAlertsFragment on Home {
    alerts {
      id
      alarmType
      deviceName
      building {
        id
        propertyName
      }
      region {
        id
        propertyName
      }
      home {
        id
        propertyName
      }
      ...ExtremeTemperatureFragment
      ...LowBatteryFragment
    }
  }
`;

const HOME_FRAGMENT = gql`
  ${REMOTELOCK_INTEGRATION_DEVICE_FRAGMENT}
  fragment HomeFragment on Home {
    homeId
    propertyName
    numResidents
    numInvitedResidents
    parentProperties {
      propertyId
      name
    }
    integrations {
      name
      numDevices
      integrationDevices {
        name
        connectivityStatus
        deviceType
        ...RemoteLockIntegrationDeviceFragment
      }
    }
    brilliantControls {
      id
      name
      numGangs
      connectivityStatus
      lastKnownCloudSync
      controlType
    }
    brilliantSwitches {
      id
      name
      connectivityStatus
    }
    brilliantPlugs {
      id
      name
      connectivityStatus
    }
    installationDetails {
      installedBrilliantControls {
        id
        name
        numGangs
      }
      installedBrilliantPlugs {
        id
        name
      }
      installedBrilliantSwitches {
        id
        name
      }
      installedIntegrationDevices {
        id
        name
        integrationType
      }
    }
    numOfflineDevices
    isHubless
    numRemotelockDevices
    reconfigurationState
    scheduledConfigurations {
      configurationTitle
      scheduledDatetime
      id
      createdDatetime
    }
    wifiCredentials {
      networkName
      networkType
      password
      securityType
    }
    isInstallIncomplete
    warnings @client {
      warningType
    }
  }
`;

const PRO_VIEWHOME_FRAGMENT = gql`
  fragment ProViewHomeFragment on Home {
    homeId
    propertyName
    homeName
    numResidents
    numInvitedResidents
    parentProperties {
      propertyId
      name
    }
    integrations {
      name
      numDevices
      integrationDevices {
        name
        connectivityStatus
        deviceType
      }
    }
    brilliantControls {
      id
      name
      numGangs
      connectivityStatus
      lastKnownCloudSync
      connectionStrength
      controlType
    }
    brilliantSwitches {
      id
      name
      connectivityStatus
    }
    brilliantPlugs {
      id
      name
      connectivityStatus
    }
    numOfflineDevices
    warnings @client {
      warningType
    }
    homePasscode
    subscription {
      currentPeriodEnd
      isProManaged
      status
    }
  }
`;

const REMOTE_LOCK_AVAILABLE_ACCESS_DEVICES_FRAGMENT = gql`
  fragment RemoteLockAvailableAccessDevicesFragment on RemoteLock {
    devices {
      id
      name
      accessibleType
      locationName
    }
    doorGroups {
      accessibleType
      id
      name
      numDevices
    }
  }
`;

export const GET_HOME_FOR_REGION = gql`
  ${PRO_VIEWHOME_FRAGMENT}
  ${HOME_ALERTS_FRAGMENT}
  query GetHomeForRegion($propertyId: String!) {
    home(propertyId: $propertyId) {
      id
      ...ProViewHomeFragment
      ...HomeAlertsFragment @client
    }
  }
`;

export const GET_HOME = gql`
  ${HOME_FRAGMENT}
  ${HOME_ALERTS_FRAGMENT}
  query GetHome($propertyId: String!) {
    home(propertyId: $propertyId) {
      id
      ...HomeFragment
      ...HomeAlertsFragment @client
    }
  }
`;

export const GET_BUILDING = gql`
  query GetBuilding($buildingId: String!) {
    building(buildingId: $buildingId) {
      id
      propertyName
      addressLine1
      addressLine2
      city
      state
      zipcode
      buildingWideSettings {
        localTimezone
        defaultHomePasscode
      }
      homes {
        id
        alerts @client {
          id
          alarmType
        }
        isInstallIncomplete
        numOfflineDevices
        warnings @client {
          warningType
        }
        isHubless
        propertyName
        reconfigurationState
        floor
        numBrilliantControls
        numBrilliantPlugs
        numBrilliantSwitches
        numIntegrations
        numResidents
        numInvitedResidents
      }
    }
  }
`;

export const GET_REGION = gql`
  query GetRegion($regionPropertyId: String!) {
    region(regionPropertyId: $regionPropertyId) {
      id
      propertyName
      numHomes
      homes {
        id
        alerts @client {
          id
          alarmType
        }
        numOfflineDevices
        warnings @client {
          warningType
        }
        propertyName
        homeName
        numBrilliantControls
        numBrilliantPlugs
        numBrilliantSwitches
        numIntegrations
        subscription {
          isProManaged
          status
        }
      }
    }
  }
`;

export const GET_REMOTE_LOCK_BUILDING = gql`
  query ${GET_REMOTELOCK_BUILDING_GQL}($buildingId: String!) {
    building(buildingId: $buildingId) {
      id
      homes {
        id
        propertyName
        remotelockDoorGroups {
          id
          doorGroup {
            id
            accessibleType
            name
            numDevices
          }
        }
        remotelockUnitAccessDevices {
          id
          device {
            id
            name
            accessibleType
          }
        }
      }
    }
  }
`;

export const GET_DOOR_GROUPS_AND_UNIT_ACCESS_DEVICES_FOR_HOME = gql`
  query ${GET_DOOR_GROUPS_AND_UNIT_ACCESS_DEVICES_FOR_HOME_GQL}($propertyId: String!) {
    home(propertyId: $propertyId) {
      id
      remotelockDoorGroups {
        id
        doorGroup {
          id
        }
      }
      remotelockUnitAccessDevices {
        id
        device {
          id
          accessibleType
        }
      }
    }
  }
`;

export const GET_RESIDENT_INFO = gql`
  query GetResidentInfo($propertyId: String!) {
    home(propertyId: $propertyId) {
      id
      residents {
        id
        familyName
        givenName
        emailAddress
        joinedPropertyDatetime
        deletedDatetime
        isAdmin
      }
      invitedResidents {
        id
        emailAddress
        expirationDatetime
      }
    }
  }
`;

export const GET_CONTROL_DETAILS = gql`
  query GetControlDetails($deviceId: String!, $propertyId: String!) {
    brilliantControl(deviceId: $deviceId, propertyId: $propertyId) {
      id
      name
      numGangs
      roomName
      lastKnownCloudSync
      lastSoftwareUpdate
      connectionStrength
      networkName
      controlType
      loads {
        dimmable
        loadType
        lowWattage
        magneticLoad
        multiWay
        name
        rooms
      }
    }
  }
`;

export const GET_SWITCH_DETAILS = gql`
  query GetSwitchDetails($deviceId: String!, $propertyId: String!) {
    brilliantSwitch(deviceId: $deviceId, propertyId: $propertyId) {
      id
      name
      roomName
      lastSoftwareUpdate
      lastKnownCloudSync
      load {
        name
        dimmable
        loadType
        lowWattage
        magneticLoad
        multiWay
        rooms
      }
    }
  }
`;

export const GET_PLUG_DETAILS = gql`
  query GetPlugDetails($deviceId: String!, $propertyId: String!) {
    brilliantPlug(deviceId: $deviceId, propertyId: $propertyId) {
      name
      roomName
      lastSoftwareUpdate
      lastKnownCloudSync
      controlsLight
    }
  }
`;

export const GET_HOME_PARENT_INFO = gql`
  query GetHomeParentInfo($propertyId: String!) {
    home(propertyId: $propertyId) {
      id
      propertyName
      parentProperties {
        propertyId
        name
      }
    }
  }
`;

export const GET_RESIDENT_CARD_INFO = gql`
  query GetResidentCardInfo($propertyId: String!, $userId: String!) {
    resident(propertyId: $propertyId, userId: $userId) {
      id
      remotelockCardNumber
      smartCardCredentials {
        id
        badgeNumber
      }
    }
  }
`;

export const GET_INVITED_RESIDENT_CARD_INFO = gql`
  query GetInvitedResidentCardInfo(
    $propertyId: String!
    $emailAddress: String!
  ) {
    invitedResident(propertyId: $propertyId, emailAddress: $emailAddress) {
      id
      remotelockCardNumber
      smartCardCredentials {
        id
        badgeNumber
      }
    }
  }
`;

export const GET_INVITED_RESIDENT_EMAILS = gql`
  query GetInvitedResidentEmails($propertyId: String!) {
    home(propertyId: $propertyId) {
      id
      invitedResidents {
        emailAddress
      }
    }
  }
`;

export const GET_PORTAL_USERS_INFO = gql`
  query GetPortalUsersInfo($organizationId: String!) {
    organization(organizationId: $organizationId) {
      id
      name
      portalUsers {
        id
        emailAddress
        givenName
        familyName
        joinedPropertyDatetime
      }
      invitedPortalUsers {
        id
        emailAddress
        expirationDatetime
      }
    }
  }
`;

export const GET_CONFIGURATIONS = gql`
  query GetConfigurations {
    configurations {
      id
      title
    }
  }
`;

export const GET_CONFIGURATION_DETAILS = gql`
  query GetConfigurationDetails($id: String!) {
    configuration(id: $id) {
      id
      title
      description
    }
  }
`;

export const GET_USER = gql`
  query GetUser($userId: String!) {
    user(userId: $userId) {
      id
      familyName
      givenName
      birthdate
      role
      organizations {
        id
        name
        numBuildings
        organizationType
      }
    }
  }
`;

export const GET_USER_ACCOUNT_INFO = gql`
  query GetUser($userId: String!) {
    user(userId: $userId) {
      id
      familyName
      givenName
      birthdate
      mfaPhoneNumber
    }
  }
`;

export const GET_ALERT_NOTIFICATION_SETTINGS = gql`
  query GetAlertNotificationSettings($userId: String!, $propertyId: String!) {
    user(userId: $userId) {
      id
      alarmNotificationSettings(propertyId: $propertyId) {
        alarmType
        notificationSettings {
          notificationMethod
          enabled
        }
      }
    }
  }
`;

export const GET_ORGANIZATION = gql`
  query GetOrganization($organizationId: String!) {
    organization(organizationId: $organizationId) {
      id
      name
      organizationType
    }
  }
`;

export const GET_ORGANIZATION_BILLING_DETAILS = gql`
  query GetOrganizationBillingDetails($organizationId: String!) {
    organization(organizationId: $organizationId) {
      id
      connectExpressOnboardingStatus
      hasSubscriptions
    }
  }
`;

export const GET_INTEGRATION_TYPES_FOR_ORGANIZATION = gql`
  query GetIntegrationTypesForOrganization($organizationId: String!) {
    organization(organizationId: $organizationId) {
      id
      integrations {
        integrationType
      }
    }
  }
`;

export const GET_INTEGRATIONS_FOR_ORGANIZATION = gql`
  ${REMOTE_LOCK_AVAILABLE_ACCESS_DEVICES_FRAGMENT}
  query GetIntegrationsForOrganization($organizationId: String!) {
    organization(organizationId: $organizationId) {
      id
      integrations {
        integrationType
        ...RemoteLockAvailableAccessDevicesFragment
      }
    }
  }
`;

export const GET_SMART_CARD_NUMBERS = gql`
  query GetSmartCardNumbers($organizationId: String!) {
    organization(organizationId: $organizationId) {
      id
      smartCardCredentialsV2 {
        id
        badgeNumber
      }
    }
  }
`;

export const GET_ORGANIZATION_SUMMARY = gql`
  query GetOrganizationSummary($organizationId: String!) {
    organization(organizationId: $organizationId) {
      id
      buildings {
        id
        addressLine1
        addressLine2
        city
        propertyName
        state
        zipcode
      }
      numHomes
    }
  }
`;

export const GET_PRO_VIEW_ORGANIZATION_SUMMARY = gql`
  query GetProViewOrganizationSummary($organizationId: String!) {
    organization(organizationId: $organizationId) {
      id
      regions {
        id
        propertyName
      }
      numHomes
    }
  }
`;

export const GET_ORGANIZATION_BUILDINGS = gql`
  query GetOrganizationBuildings($organizationId: String!) {
    organization(organizationId: $organizationId) {
      id
      buildings {
        id
        addressLine1
        addressLine2
        city
        propertyName
        state
        zipcode
      }
      regions {
        id
        propertyName
      }
    }
  }
`;

export const GET_ALERTS_FOR_ORGANIZATION = gql`
  ${EXTREME_TEMPERATURE_FRAGMENT}
  ${LOW_BATTERY_FRAGMENT}
  query GetAlertsForOrganization($organizationId: String!) {
    organization(organizationId: $organizationId) {
      id
      alerts {
        id
        alarmType
        deviceName
        building {
          id
          propertyName
        }
        region {
          id
          propertyName
        }
        home {
          id
          propertyName
        }
        ...ExtremeTemperatureFragment
        ...LowBatteryFragment
      }
    }
  }
`;

export const UPDATE_WIFI_CREDENTIALS = gql`
  mutation UpdateWifiCredentials(
    $propertyId: String!
    $networkName: String!
    $networkType: String!
    $password: String!
    $securityType: String
  ) {
    updateWifiCredentials(
      input: {
        propertyId: $propertyId
        networkName: $networkName
        networkType: $networkType
        password: $password
        securityType: $securityType
      }
    ) {
      wifiCredentials {
        networkName
        networkType
        password
        securityType
      }
    }
  }
`;

export const RESET_INSTALLATION = gql`
  ${HOME_FRAGMENT}
  ${HOME_ALERTS_FRAGMENT}
  mutation ${RESET_INSTALLATION_GQL}(
    $propertyId: String!
  ) {
    ${RESET_INSTALLATION_GQL}(
      input: {
        propertyId: $propertyId
      }
    ) {
      home {
        id
        ...HomeFragment
        ...HomeAlertsFragment
      }
    }
  }
  `;

export const UPDATE_CARD_CREDENTIALS = gql`
  mutation ${UPDATE_CARD_CREDENTIALS_SQL}(
    $propertyId: String!
    $emailAddress: String!
    $cardNumber: String!
    $smartCardId: String!
  ) {
    ${UPDATE_CARD_CREDENTIALS_SQL}(
      input: {
        propertyId: $propertyId
        emailAddress: $emailAddress
        cardNumber: $cardNumber
        smartCardId: $smartCardId
      }
    ) {
      cardNumber
      smartCardCredential {
        id
        badgeNumber
      }
    }
  }
  `;

export const CREATE_ORGANIZATION = gql`
  mutation ${CREATE_ORGANIZATION_GQL}(
    $organizationName: String!
    $organizationType: String!
  ) {
    ${CREATE_ORGANIZATION_GQL}(
      input: {
        organizationName: $organizationName
        organizationType: $organizationType
      }
    ) {
      organization {
        id
        name
        numBuildings
      }
    }
  }
`;

export const REBOOT_BRILLIANT_CONTROL = gql`
  mutation ${REBOOT_BRILLIANT_CONTROL_GQL}(
    $propertyId: String!
    $deviceId: String!
  ) {
    ${REBOOT_BRILLIANT_CONTROL_GQL}(
      input: {
        propertyId: $propertyId
        deviceId: $deviceId
      }
    ) {
      success
    }
  }
`;

export const UPDATE_HOME_PASSCODE = gql`
  mutation ${UPDATE_HOME_PASSCODE_GQL}(
    $homePasscode: String!
    $propertyId: String!
  ) {
    ${UPDATE_HOME_PASSCODE_GQL}(
      input: {
        homePasscode: $homePasscode
        propertyId: $propertyId
      }
    ) {
      homePasscode
    }
  }
`;

export const ADD_HOME_FOR_REGION = gql`
  mutation ${ADD_HOME_FOR_REGION_GQL}(
    $regionPropertyId: String!
    $homeName: String!
  ) {
    ${ADD_HOME_FOR_REGION_GQL}(
      input: {
        regionPropertyId: $regionPropertyId
        homeName: $homeName
      }
    ) {
      home {
        id
        homeName
      }
    }
  }
`;

export const CREATE_BUILDING = gql`
  mutation ${CREATE_BUILDING_GQL}(
    $buildingName: String!
    $timeZone: String!
    $addressLine1: String!
    $addressLine2: String
    $city: String!
    $state: String!
    $zipcode: String!
    $homePasscode: String!
  ) {
    ${CREATE_BUILDING_GQL}(
      input: {
        buildingName: $buildingName
        timeZone: $timeZone
        addressLine1: $addressLine1
        addressLine2: $addressLine2
        city: $city
        state: $state
        zipcode: $zipcode
        homePasscode: $homePasscode
      }
    ) {
      building {
        id
        addressLine1
        addressLine2
        city
        propertyName
        state
        zipcode
      }
    }
  }
`;

export const EDIT_BUILDING = gql`
  mutation ${EDIT_BUILDING_GQL}(
    $propertyId: String!
    $buildingName: String!
    $timeZone: String!
    $addressLine1: String!
    $addressLine2: String
    $city: String!
    $state: String!
    $zipcode: String!
    $homePasscode: String!
  ) {
    ${EDIT_BUILDING_GQL}(
      input: {
        propertyId: $propertyId
        buildingName: $buildingName
        timeZone: $timeZone
        addressLine1: $addressLine1
        addressLine2: $addressLine2
        city: $city
        state: $state
        zipcode: $zipcode
        homePasscode: $homePasscode
      }
    ) {
      building {
        id
        addressLine1
        addressLine2
        city
        propertyName
        state
        zipcode
      }
    }
  }
`;

export const EDIT_ORGANIZATION = gql`
  mutation ${EDIT_ORGANIZATION_GQL}(
    $organizationName: String!
  ) {
    ${EDIT_ORGANIZATION_GQL}(
      input: {
        organizationName: $organizationName
      }
    ) {
      organization {
        id
        name
        numBuildings
      }
    }
  }
`;

export const REMOVE_PROVIEW_HOME = gql`
  mutation ${REMOVE_PROVIEW_HOME_GQL}(
    $propertyId: String!
  ) {
    ${REMOVE_PROVIEW_HOME_GQL}(
      input: {
        propertyId: $propertyId
      }
    ) {
      success
    }
  }
`;

export const ADD_REMOTELOCK_DEVICES_TO_PROPERTIES = gql`
  mutation ${ADD_REMOTELOCK_DEVICES_TO_PROPERTIES_GQL}(
    $remotelockDeviceAssignments: [AddRemoteLockDeviceAssignment]!
  ) {
    ${ADD_REMOTELOCK_DEVICES_TO_PROPERTIES_GQL}(
      input: { remotelockDeviceAssignments: $remotelockDeviceAssignments }
    ) {
      count
    }
  }
`;

export const REMOVE_REMOTELOCK_DEVICES_TO_PROPERTIES = gql`
  mutation ${REMOVE_REMOTELOCK_DEVICES_TO_PROPERTIES_GQL}(
    $remotelockDeviceRemovals: [RemoveRemoteLockDeviceAssignment]!
  ) {
    ${REMOVE_REMOTELOCK_DEVICES_TO_PROPERTIES_GQL}(
      input: { remotelockDeviceRemovals: $remotelockDeviceRemovals }
    ) {
      recordIds
    }
  }
`;

export const UPDATE_USER_PROPERTY_NOTIFICATION_SETTING = gql`
  mutation updateUserPropertyNotificationSetting(
    $userId: String!
    $alarmType: ClimateAlarmType!
    $notificationMethod: NotificationMethod!
    $enabled: Boolean!
    $propertyId: String!
  ) {
    updateUserNotificationSetting(
      input: {
        userId: $userId
        alarmType: $alarmType
        notificationMethod: $notificationMethod
        enabled: $enabled
        propertyId: $propertyId
      }
    ) {
      alarmNotificationSettings {
        alarmType
        notificationSettings {
          notificationMethod
          enabled
        }
      }
    }
  }
`;

export const CREATE_CONSUMER_CHECKOUT_URL = gql`
  mutation createHomeSubscriptionCheckout($homeId: String!, $plan: String!) {
    createHomeSubscriptionCheckout(input: { homeId: $homeId, plan: $plan }) {
      checkoutUrl
    }
  }
`;

export const CREATE_PRO_BILLING_PORTAL_SESSION = gql`
  mutation createProBillingPortalSession($orgId: String!) {
    createProBillingPortalSession(input: { orgId: $orgId }) {
      portalUrl
    }
  }
`;

export const CREATE_CONNECT_DASHBOARD_LINK = gql`
  mutation createConnectDashboardLink($orgId: String!) {
    createConnectDashboardLink(input: { orgId: $orgId }) {
      dashboardUrl
    }
  }
`;

export const CREATE_CONNECT_ONBOARDING_LINK = gql`
  mutation createConnectOnboardingLink($orgId: String!) {
    createConnectOnboardingLink(input: { orgId: $orgId }) {
      onboardingUrl
    }
  }
`;

export const CREATE_PRO_HOME_SUBSCRIPTION_CHECKOUT = gql`
  mutation createProHomeSubscriptionCheckout(
    $orgId: String!
    $homeId: String!
  ) {
    createProHomeSubscriptionCheckout(
      input: { orgId: $orgId, homeId: $homeId }
    ) {
      checkoutUrl
    }
  }
`;

export const CREATE_SUBSCRIPTION_CANCEL_PORTAL_SESSION = gql`
  mutation createSubscriptionCancelPortalSession($homeId: String!) {
    createSubscriptionCancelPortalSession(input: { homeId: $homeId }) {
      portalUrl
    }
  }
`;

export const CREATE_SUBSCRIPTION_REACTIVATE_PORTAL_SESSION = gql`
  mutation createSubscriptionReactivatePortalSession($homeId: String!) {
    createSubscriptionReactivatePortalSession(input: { homeId: $homeId }) {
      portalUrl
    }
  }
`;
